import { inject, observer } from 'mobx-react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { AnalyticsStore, mergeQueryString, QueryRoute, updateSearch } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { Layer } from 'bernie-view';
import { LocalizedComponentProps, LocalizedText } from 'bernie-l10n';

import { SearchResultsStore } from 'stores/search-results-store';

import { UitkCard, UitkCardContentSection, UitkCardLink } from 'uitk-react-cards';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from 'uitk-react-images';
import { UitkLink } from 'uitk-react-link';
import { UitkSecondaryButton } from 'uitk-react-button';

import { handleMapTrigger } from './map-utils';

import { SearchResultsMap } from 'components/search/map';

const LinkTracked = ClickTracker(Link);
const ButtonTracked = ClickTracker(UitkSecondaryButton);

export interface MapPreviewProps extends LocalizedComponentProps, Partial<RouteComponentProps> {
  analytics?: AnalyticsStore;
  className?: string;
  searchResults?: SearchResultsStore;
  mapImage?: string;
  isActive: boolean;
}

@withRouter
@inject('searchResults', 'analytics')
@observer
export class MapPreview extends React.Component<MapPreviewProps> {
  private mapBtn;

  constructor(props) {
    super(props);

    this.onMapDialogClosed = this.onMapDialogClosed.bind(this);
    this.mapBtn = React.createRef();
  }

  public static defaultProps = {
    isActive: false,
  };

  public render() {
    const { isActive, searchResults, mapImage, analytics, history, location } = this.props;
    const { isLoading, isError, isEmpty } = searchResults;
    const map = searchResults && searchResults.map;
    const mapOpenQuery = mergeQueryString(location.search, { pwaOverlay: 'map' });
    const linkTo = {
      pathname: location.pathname,
      search: mapOpenQuery,
    };
    const extraProps =
      isActive && map && Object.keys(map).length > 0
        ? {
            tag: Link,
            to: linkTo,
            role: 'button',
          }
        : {};

    const triggerRef = mapBtn => {
      this.mapBtn = mapBtn;
    };

    return (
      <>
        <QueryRoute query={{ pwaOverlay: 'map' }}>
          <Layer id="tools-map">
            <SearchResultsMap onDismiss={this.onMapDialogClosed} />
          </Layer>
        </QueryRoute>
        {mapImage && (
          <UitkCard overflow={true}>
            <UitkFigure ratio={UitkFigureAspectRatioType.R21_9} className="uitk-card-roundcorner-top-right uitk-card-roundcorner-top-left">
              <UitkImage alt="" src={mapImage} />
            </UitkFigure>
            <UitkLink align="center">
              <UitkCardContentSection>
                <section data-stid="mapClickButton" className="uitk-button" onClickCapture={handleMapTrigger(history, location, analytics)}>
                  <LocalizedText message="hotels.searchResults.openMapDialog.label" />
                </section>
              </UitkCardContentSection>
            </UitkLink>
            <UitkCardLink>
              <LinkTracked linkName="Launch dynamic map" rfrr="Module.MapOpen.Link" to={linkTo} innerRef={triggerRef}>
                <LocalizedText message="hotels.searchResults.openMapDialog.label" />
              </LinkTracked>
            </UitkCardLink>
          </UitkCard>
        )}
        {!mapImage && (
          <ButtonTracked
            data-stid="open-dynamic-map"
            disabled={!isActive || isLoading || isError || isEmpty || null}
            linkName="Launch dynamic map"
            rfrr="Module.MapOpen.Link"
            icon="map"
            isFullWidth={true}
            {...extraProps}
            innerRef={triggerRef}
          >
            <LocalizedText message="hotels.searchResults.openMapDialog.label" />
          </ButtonTracked>
        )}
      </>
    );
  }

  private onMapDialogClosed(): void {
    const { history, location } = this.props;
    updateSearch({
      history,
      location,
      newParams: {
        pwaOverlay: undefined,
      },
    });

    if (this.mapBtn && typeof this.mapBtn.focus === 'function') {
      this.mapBtn.focus();
    }
  }
}
